/**
 * The line-level fields of one line: Customer Reference #, Line Item Notes, Line Level PO and the
 * Domestic Requested checkbox with its tooltip, plus a "Batch Details" link for users with the
 * `Select_Batches_View` permission. One component serves the expanded rows of the cart, shopping
 * lists, quote details, Easy Quote, checkout review, order confirmation, order details and open
 * orders.
 *
 * Position: the caller passes the row as `itemData` and receives the edited row through
 * `handleLineLevelItemsChanged`, then writes it to its own state or API (for example
 * `components/QuoteDetails/Helpers/EditProductsTableComponents/TableExpandContent.tsx` →
 * `handleLineLevelItemsChanged`). `pageType` values are in `components/Shared/Model/PageType.ts`.
 *
 * Rules a change must keep:
 * - Row shapes name the fields differently: the reference number is `customerReferenceNumber` or
 *   `customerRefNumber`, the notes `lineItemNotes` or `notes`. The edited row keeps the reference
 *   number under the key the row used, but always carries the notes as `notes` and always sets
 *   `domesticRequired`; `purchaseOrderNumber` is set only when the row has that key. Every
 *   receiver reads `notes`.
 * - Typing is debounced (`debouncedTime`, 500 ms by default). The callback fires once the
 *   debounced values differ from the row's, and carries all four fields at once.
 * - When `itemData` changes (compared as JSON), the inputs reset to the new row's values: the
 *   caller's row is the source of truth.
 * - `pageType="order"` hides the notes and Domestic Requested.
 * - `quoteLineItem` (the read-only quote details view) shows "add this info in checkout" as the
 *   value of an empty reference, notes or PO field.
 * - Input lengths are capped by `MAX_FIELD_LENGTHS` from `@ryerson/frontend.common`.
 */
import React from "react";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";

import { Checkbox, ErpInput } from "@ryerson/frontend.form-controls";

import { useDebounce } from "@components/Hooks";
import { useApplication } from "@ryerson/frontend.application";

import { content as allLanguages } from "./Content";
import { toPascalCase } from "@components/Helper/common";
import getPermission from "@components/Shared/UserPermissions";
import { MAX_FIELD_LENGTHS } from "@ryerson/frontend.common";
import { ProductLineItemsProps } from "./ProductLineItem.model";
import rgba from "polished/lib/color/rgba";
import { useTheme } from "@ryerson/frontend.theme";
import { Popover } from "@ryerson/frontend.modal";
import { Icon } from "@ryerson/frontend.assets";
import { Media } from "@ryerson/frontend.next";

// Begin Component
/**
 * Renders the line-level fields for one row (default export, imported as `ProductLineItems`).
 * See the file header for the callback contract.
 */
const CustomerNotesSection: React.FC<ProductLineItemsProps> = ({
	id,
	testingId,
	itemData,
	handleLineLevelItemsChanged,
	pageType,
	isEdit = true,
	debouncedTime = 500,
	showToolTip = true,
	quoteLineItem = false,
	handleOnClickCheckInventory,
}) => {
	const {
		localization: { language },
		hasPermission,
	} = useApplication();
	const { theme } = useTheme();
	const { lightGray, primaryBrand, text } = theme.colors.primary;
	const content = allLanguages[language];

	const [isMounted, setIsMounted] = React.useState<boolean>(false);
	// const [bindExternal, setBindExternal] = React.useState<boolean>(false);

	const COMPANY = process.env.NEXT_PUBLIC_COMPANY;
	const refNumberKey = React.useRef<string>("");
	const purchaseOrderNumberKey = React.useRef<string>("");

	// The getters below read the current row, and record in the refs which key the row uses, so
	// the edited row is written back under the same key.
	const getCustomerReferenceNumber = () => {
		if ("customerReferenceNumber" in itemData) {
			refNumberKey.current = "customerReferenceNumber";
			return quoteLineItem
				? itemData?.customerReferenceNumber || content.addInfo
				: itemData?.customerReferenceNumber || "";
		} else if ("customerRefNumber" in itemData) {
			refNumberKey.current = "customerRefNumber";
			return itemData?.customerRefNumber || "";
		} else {
			refNumberKey.current = "";
			return "";
		}
	};

	const getItemNotes = () => {
		if ("lineItemNotes" in itemData && typeof itemData.lineItemNotes === "string") {
			if (quoteLineItem) {
				return itemData?.lineItemNotes || content.addInfo || "";
			} else {
				return itemData?.lineItemNotes || "";
			}
		} else if ("notes" in itemData && typeof itemData.notes === "string") {
			return itemData.notes;
		} else {
			return "";
		}
	};
	const getLineLevelPO = () => {
		if ("purchaseOrderNumber" in itemData) {
			purchaseOrderNumberKey.current = "purchaseOrderNumber";
			return quoteLineItem
				? itemData?.purchaseOrderNumber || content.addInfo
				: itemData?.purchaseOrderNumber || "";
		} else {
			purchaseOrderNumberKey.current = "";
			return "";
		}
	};

	const getDomesticRequired = () => {
		return "domesticRequired" in itemData ? itemData.domesticRequired || false : false;
	};

	// Check (JSON) Key in Items Data (potential different) key to adapt to variety of API's)
	const [referenceNumber, setReferenceNumber] = React.useState<string>(
		getCustomerReferenceNumber()
	);
	const [itemNotes, setItemNotes] = React.useState<string>(getItemNotes());
	const [lineLevelPO, setLineLevelPO] = React.useState<string>(getLineLevelPO());
	const [domesticRequired, setDomesticRequired] = React.useState<boolean>(getDomesticRequired());

	const handleCheckBoxChange = (value: string, checked: boolean): void => {
		//checked && handleCheckInventory();
		setDomesticRequired(checked);
	};

	// Debounced Values
	const referenceNumberDebounced = useDebounce(referenceNumber, debouncedTime);
	const itemNotesDebounced = useDebounce(itemNotes, debouncedTime);
	const lineLevelPODebounced = useDebounce(lineLevelPO, debouncedTime);
	const domesticRequiredDebounced = useDebounce(domesticRequired, debouncedTime);

	const hasUpdatedItems = () => {
		return (
			referenceNumberDebounced !== getCustomerReferenceNumber() ||
			itemNotesDebounced !== getItemNotes() ||
			lineLevelPODebounced !== getLineLevelPO() ||
			domesticRequiredDebounced !== getDomesticRequired()
		);
	};
	// Debounce Value(s) : Returns a copy of updated Item Data to the component callBack
	// `isMounted` stops a callback during the first render.
	React.useEffect(() => {
		const wasUpdated = hasUpdatedItems();
		if (isMounted && wasUpdated) {
			let updatedItem = { ...itemData };
			!!refNumberKey.current &&
				(updatedItem = {
					...updatedItem,
					[refNumberKey.current]: referenceNumberDebounced,
				});

			updatedItem = { ...updatedItem, notes: itemNotesDebounced };
			!!purchaseOrderNumberKey.current &&
				(updatedItem = { ...updatedItem, purchaseOrderNumber: lineLevelPODebounced });
			updatedItem = { ...updatedItem, domesticRequired: domesticRequiredDebounced };

			!!handleLineLevelItemsChanged && handleLineLevelItemsChanged(updatedItem);
		}
	}, [
		referenceNumberDebounced,
		itemNotesDebounced,
		lineLevelPODebounced,
		domesticRequiredDebounced,
	]);

	// Mount
	React.useEffect(() => {
		setIsMounted(true);
	}, []);

	React.useEffect(() => {
		if (isMounted) {
			referenceNumber !== getCustomerReferenceNumber() &&
				setReferenceNumber(getCustomerReferenceNumber());
			itemNotes !== getItemNotes() && setItemNotes(getItemNotes());
			lineLevelPO !== getLineLevelPO() && setLineLevelPO(getLineLevelPO());
			domesticRequired !== getDomesticRequired() &&
				setDomesticRequired(getDomesticRequired());
		}
	}, [JSON.stringify(itemData)]);

	const renderProductLineItemsSection = (isMobile: boolean = false) => {
		return (
			<>
				<>
					<Flex css={{ marginBottom: "7px" }}>
						<Typography>{content.customerReferenceNo}</Typography>
					</Flex>
					<Flex css={{ marginBottom: "18px" }}>
						<FlexItem css={{ width: "100%" }}>
							<>
								<ErpInput
									maxLength={MAX_FIELD_LENGTHS.CUSTOMER_REFERENCE}
									disabled={!isEdit}
									inputType="text"
									value={referenceNumber}
									onChange={(e) => setReferenceNumber(e.target.value)}
									language={language}
									dataTestId="customer-reference-number-input"
								/>
							</>
						</FlexItem>
					</Flex>
				</>

				{pageType !== "order" && (
					<>
						<Flex css={{ marginBottom: "7px" }}>
							<Typography>{content.lineItemNotes}</Typography>
						</Flex>
						<Flex css={{ marginBottom: "18px" }}>
							<FlexItem css={{ width: "100%" }}>
								<ErpInput
									maxLength={MAX_FIELD_LENGTHS.LINE_LEVEL_NOTES}
									disabled={!isEdit}
									inputType="text"
									value={itemNotes}
									onChange={(e) => setItemNotes(e.target.value)}
									language={language}
									dataTestId="line-item-notes-input"
								/>
							</FlexItem>
						</Flex>
					</>
				)}

				<>
					<Flex css={{ marginBottom: "7px" }}>
						<Typography>{content.lineLevelPO}</Typography>
					</Flex>
					<Flex css={{ marginBottom: "18px" }}>
						<FlexItem css={{ width: "100%" }}>
							<ErpInput
								maxLength={MAX_FIELD_LENGTHS.LINE_LEVEL_PO}
								disabled={!isEdit}
								value={lineLevelPO}
								inputType="text"
								onChange={(e) => setLineLevelPO(e.target.value)}
								language={language}
								dataTestId="line-level-po-input"
							/>
						</FlexItem>
					</Flex>
				</>

				{pageType !== "order" && (
					<>
						<Flex alignItems="center" css={{ marginBottom: "15px" }}>
							<FlexItem>
								<Checkbox
									onChange={handleCheckBoxChange}
									checked={domesticRequired}
									value=""
									language={language}
									disabled={!isEdit}
									id={testingId}
									dataTestId="quote-details-item-domestic-required-checkbox"
								/>
							</FlexItem>
							<FlexItem>
								<Typography>{content.domesticRequired}</Typography>
							</FlexItem>

							<FlexItem css={{ paddingLeft: 20, width: "50px" }}>
								<Popover
									offset={[0, 5]}
									caretColor={theme.colors.primary.white}
									alignment={"bottom"}
									className={`trigger info-icon-${id}`}
									hover={true}
									trigger={
										<span>
											<Icon icon="help" size="md" color={primaryBrand} />
										</span>
									}
									dataTestId="product-line-items-help-popover"
								>
									<div
										css={{
											border: `1px solid ${rgba(lightGray, 0.6)}`,
											padding: 20,
											width: 300,
										}}
									>
										<Typography
											autoResize={false}
											size="sm"
											color={isMobile ? text : lightGray}
										>
											{content.toolTip.replace(
												/<COMPANY>/,
												toPascalCase(COMPANY || "")
											)}
										</Typography>
									</div>
								</Popover>
							</FlexItem>
						</Flex>
					</>
				)}

				{getPermission(hasPermission, "Select_Batches_View") &&
					handleOnClickCheckInventory && (
						<FlexItem
							css={{ marginBottom: "20px" }}
							onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
								handleOnClickCheckInventory(event)
							}
							data-testid="inventory-details-click"
						>
							<Typography color={theme.colors.primary.primaryBrand}>
								{content.inventoryDetails}
							</Typography>
						</FlexItem>
					)}
			</>
		);
	};

	return (
		<>
			<Media lessThan="lg">{renderProductLineItemsSection(true)}</Media>
			<Media greaterThanOrEqual="lg">{renderProductLineItemsSection(false)}</Media>
		</>
	);
};

export default CustomerNotesSection;
